<template>
  <v-card
    class="pricing-card d-flex flex-column fill-height"
    :class="{
      'pricing-card--popular': popular && !active,
      'pricing-card--active': active,
    }"
    :ripple="false"
    :hover="!readOnly"
    @click="onCardClick"
  >
    <v-skeleton-loader
      v-if="loading"
      type="article, list-item-three-line, button"
      class="pa-4"
    />
    <template v-else>
      <div class="pricing-card__badge-row">
        <v-chip
          v-if="active"
          small
          color="success"
          text-color="white"
          class="text-none font-weight-medium"
        >
          {{ $t('subscriptions.currentPlan') }}
        </v-chip>
        <v-chip
          v-else-if="popular"
          small
          color="primary"
          text-color="white"
          class="text-none font-weight-medium"
        >
          {{ $t('subscriptions.popular') }}
        </v-chip>
      </div>

      <div class="pricing-card__icon-wrap">
        <v-avatar
          size="52"
          :color="active || popular ? 'primary' : undefined"
          :class="active || popular ? '' : 'pricing-card__icon-muted'"
        >
          <v-icon :color="active || popular ? 'white' : 'primary'" size="28">
            {{ planIcon }}
          </v-icon>
        </v-avatar>
      </div>

      <div class="text-center px-4">
        <div class="text-h5 font-weight-bold text-uppercase tracking-wide">
          {{ plan.name }}
        </div>
        <div
          v-if="planTagline"
          class="caption text--secondary mt-1 px-2"
        >
          {{ planTagline }}
        </div>
      </div>

      <div class="text-center my-6">
        <template v-if="plan.price">
          <div class="pricing-card__price">
            {{ plan.price.display }}
          </div>
          <div class="text--secondary">
            {{ $t('subscriptions.perMonth', [plan.price.currency.toUpperCase()]) }}
          </div>
        </template>
        <template v-else>
          <div class="pricing-card__price text-capitalize">
            {{ plan.priceLabel || plan.metadata && plan.metadata['priceLabel_' + $i18n.locale] || $t('subscriptions.free') }}
          </div>
          <div class="text--secondary">
            {{ $t('subscriptions.perMonthShort') }}
          </div>
        </template>
      </div>

      <v-divider class="mx-6" />

      <v-list class="pricing-card__features flex-grow-1 py-4 px-2">
        <v-list-item
          v-for="(feature, index) of features"
          :key="index"
          dense
          class="px-4"
        >
          <v-list-item-icon class="mr-3 my-2">
            <v-icon
              small
              color="success"
            >
              check_circle
            </v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title class="pricing-card__feature-label text-wrap">
              {{ feature.label }}
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
        <div
          v-if="plan.metadata && plan.metadata['footnote_' + $i18n.locale]"
          class="caption text--secondary text-center px-4 mt-2"
        >
          {{ plan.metadata['footnote_' + $i18n.locale] }}
        </div>
      </v-list>

      <div class="pa-5 pt-2">
        <template v-if="readOnly">
          <v-btn
            v-if="active"
            block
            depressed
            disabled
            class="text-none"
          >
            {{ $t('subscriptions.currentPlan') }}
          </v-btn>
          <v-btn
            v-else-if="plan.name === 'BUSINESS'"
            block
            outlined
            color="primary"
            class="text-none"
            @click.stop="$emit('individual')"
          >
            {{ $t('subscriptions.contactSales') }}
            <v-icon
              small
              class="ml-2"
            >
              open_in_new
            </v-icon>
          </v-btn>
          <StripeManageButton
            v-else-if="plan.id !== 'free'"
            v-model="stripeLoading"
            :message="'subscriptions.changePlanCta'"
            color="primary"
            class="text-none"
            :disabled="!stripeLoading && stripeDisabled"
          />
          <v-btn
            v-else
            block
            outlined
            disabled
            class="text-none"
          >
            {{ $t('subscriptions.free') }}
          </v-btn>
        </template>
        <template v-else>
          <v-btn
            v-if="plan.name === 'BUSINESS'"
            block
            :color="active ? 'primary' : undefined"
            :outlined="!active"
            :depressed="active"
            class="text-none"
            @click.stop="$emit('individual')"
          >
            {{ $t('subscriptions.contactSales') }}
          </v-btn>
          <v-btn
            v-else
            block
            :color="active || popular ? 'primary' : undefined"
            :outlined="!(active || popular)"
            :depressed="active || popular"
            class="text-none"
            @click.stop="$emit('continue', plan)"
          >
            {{ ctaLabel }}
          </v-btn>
        </template>
      </div>
    </template>
  </v-card>
</template>

<script lang="ts">
    import Vue, {PropType} from 'vue';
    import StripeManageButton from "@/components/account/StripeManageButton.vue";
    import {EventBus} from "@/utils/EventBus";
    import {StripeProductLike, StripeRecurringProduct} from "@/model/Stripe";
    import {TranslateResult} from "vue-i18n";

    export default Vue.extend({
        name: 'SubscriptionCard',
        components: {StripeManageButton},
        props: {
            active: {
                type: Boolean,
                default: false
            },
            popular: {
                type: Boolean,
                default: false
            },
            loading: {
                type: Boolean,
                default: true
            },
            plan: {
                type: Object as PropType<StripeRecurringProduct | StripeProductLike>,
                default: (): StripeProductLike => ({
                    id: 'null',
                    name: '',
                    priceLabel: '',
                    metadata: {},
                })
            },
            readOnly: {
                type: Boolean,
                default: false
            }
        },
        data: () => ({
            stripeLoading: false,
            stripeDisabled: false
        }),
        computed: {
            planIcon(): string {
                const name = (this.plan.name || '').toUpperCase();
                if (this.plan.id === 'free') return 'auto_awesome';
                if (name.includes('BUSINESS')) return 'apartment';
                if (name.includes('PRO') || name.includes('PLUS') || name.includes('UNLIMITED')) {
                    return 'workspace_premium';
                }
                return 'bolt';
            },
            planTagline(): string {
                const meta = this.plan.metadata;
                if (!meta) return '';
                return meta['description_' + this.$i18n.locale] || '';
            },
            features(): { label: TranslateResult }[] {
                const ret: { label: TranslateResult }[] = [];
                const price = (this.plan as StripeRecurringProduct).price;
                if (price?.transform_quantity) {
                    ret.push({
                        label: this.$t('subscriptions.upToInspirations', [price.transform_quantity.divide_by])
                    });
                }
                if (this.plan.id !== 'free') {
                    ret.push({label: this.$t('subscriptions.features.keywordAnalysis')});
                    ret.push({label: this.$t('subscriptions.features.inspiration')});
                } else {
                    ret.push({label: this.$t('subscriptions.features.seoScore')});
                    ret.push({label: this.$t('subscriptions.features.export')});
                }
                return ret;
            },
            ctaLabel(): TranslateResult {
                if (this.plan.id === 'free') {
                    return this.$t('subscriptions.startFree');
                }
                if (this.active) {
                    return this.$t('subscriptions.continueWithPlan', [this.plan.name]);
                }
                return this.$t('subscriptions.getStarted');
            }
        },
        watch: {
            stripeLoading() {
                EventBus.$emit("stripeLoading");
            }
        },
        created(): void {
            EventBus.$on("stripeLoading", () => {
                this.stripeDisabled = true;
            });
        },
        methods: {
            onCardClick(): void {
                if (this.readOnly || this.loading) return;
                this.$emit('select');
            }
        }
    });
</script>

<style lang="sass" scoped>
.pricing-card
    border-radius: 16px !important
    overflow: hidden
    position: relative
    transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease

    &--popular
        border: 2px solid var(--v-primary-base) !important

        @media (min-width: 960px)
            transform: translateY(-12px)

    &--active
        border: 2px solid var(--v-success-base) !important

.pricing-card__badge-row
    min-height: 36px
    display: flex
    justify-content: center
    padding: 12px 12px 0

.pricing-card__icon-wrap
    display: flex
    justify-content: center
    margin: 8px 0 12px

.pricing-card__icon-muted
    background: rgba(8, 135, 220, 0.12) !important

.theme--dark .pricing-card__icon-muted
    background: rgba(8, 135, 220, 0.22) !important

.pricing-card__price
    font-size: 2.25rem
    font-weight: 700
    letter-spacing: -0.02em
    line-height: 1.1

.pricing-card__features
    background: transparent !important

.pricing-card__feature-label
    font-size: 0.875rem !important
    font-weight: 500 !important
    white-space: normal !important
    height: auto !important
    line-height: 1.35 !important
</style>
